'use client';

import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useStore } from '@base-ui/utils/store';
import { CalendarGrid } from '@mui/x-scheduler-headless/calendar-grid';
import { isWeekend } from '@mui/x-scheduler-headless/use-adapter';
import { useAdapterContext } from '@mui/x-scheduler-headless/use-adapter-context';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-headless/use-event-calendar-store-context';
import { eventCalendarOccurrencePlaceholderSelectors } from '@mui/x-scheduler-headless/event-calendar-selectors';
import { schedulerOtherSelectors } from '@mui/x-scheduler-headless/scheduler-selectors';
import { DayGridEvent } from "../event/index.mjs";
import { EventDialogTrigger } from "../event-dialog/index.mjs";
import { useEventDialogContext } from "../event-dialog/EventDialog.mjs";
import { EventSkeleton } from "../event-skeleton/index.mjs";
import { useEventCalendarStyledContext } from "../../../event-calendar/EventCalendarStyledContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const EVENT_HEIGHT = 22;
const DayTimeGridAllDayEventsCell = styled(CalendarGrid.DayCell, {
  name: 'MuiEventCalendar',
  slot: 'DayTimeGridAllDayEventsCell'
})(({
  theme
}) => ({
  flexGrow: 1,
  flexShrink: 0,
  flexBasis: 0,
  minWidth: 0,
  padding: theme.spacing(0.5),
  display: 'grid',
  gridTemplateRows: 'repeat(var(--row-count), minmax(auto, 18px))',
  gap: theme.spacing(0.5),
  lineHeight: '18px',
  minHeight: `calc(var(--row-count, 0) * ${EVENT_HEIGHT}px + ${theme.spacing(0.5)})`,
  '&[data-weekend]': {
    backgroundColor: (theme.vars || theme).palette.action.hover
  }
}));
const DayTimeGridAllDayEventsCellEvents = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'DayTimeGridAllDayEventsCellEvents'
})(({
  theme
}) => ({
  position: 'relative',
  display: 'grid',
  gap: theme.spacing(0.5)
}));
const DayTimeGridAllDayEventContainer = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'DayTimeGridAllDayEventContainer'
})({
  display: 'contents'
});
export function DayGridCell(props) {
  const {
    day,
    row
  } = props;

  // Context hooks
  const adapter = useAdapterContext();
  const store = useEventCalendarStoreContext();
  const {
    onOpen: startEditing
  } = useEventDialogContext();
  const {
    classes
  } = useEventCalendarStyledContext();

  // Ref hooks
  const cellRef = React.useRef(null);

  // Selector hooks
  const isCreatingAnEvent = useStore(store, eventCalendarOccurrencePlaceholderSelectors.isCreatingInDayCell, day.value);
  const placeholder = CalendarGrid.usePlaceholderInDay(day.value, row);
  const isLoading = useStore(store, schedulerOtherSelectors.isLoading);
  React.useEffect(() => {
    if (!isCreatingAnEvent || !placeholder || !cellRef.current) {
      return;
    }
    startEditing(cellRef, placeholder);
  }, [isCreatingAnEvent, placeholder, startEditing]);
  return /*#__PURE__*/_jsx(DayTimeGridAllDayEventsCell, {
    className: classes.dayTimeGridAllDayEventsCell,
    ref: cellRef,
    value: day.value,
    addPropertiesToDroppedEvent: addPropertiesToDroppedEvent,
    style: {
      '--row-count': row.maxIndex
    },
    "aria-labelledby": `DayTimeGridHeaderCell-${adapter.getDate(day.value)} DayTimeGridAllDayEventsHeaderCell`,
    role: "gridcell",
    "data-weekend": isWeekend(adapter, day.value) || undefined,
    children: /*#__PURE__*/_jsxs(DayTimeGridAllDayEventsCellEvents, {
      className: classes.dayTimeGridAllDayEventsCellEvents,
      children: [isLoading && /*#__PURE__*/_jsx(EventSkeleton, {
        "data-variant": "day-grid"
      }), day.withPosition.map(occurrence => {
        if (occurrence.position.isInvisible) {
          return /*#__PURE__*/_jsx(DayGridEvent, {
            occurrence: occurrence,
            variant: "invisible"
          }, occurrence.key);
        }
        return /*#__PURE__*/_jsx(EventDialogTrigger, {
          occurrence: occurrence,
          children: /*#__PURE__*/_jsx(DayGridEvent, {
            occurrence: occurrence,
            variant: "filled"
          })
        }, occurrence.key);
      }), placeholder != null && /*#__PURE__*/_jsx(DayTimeGridAllDayEventContainer, {
        className: classes.dayTimeGridAllDayEventContainer,
        children: /*#__PURE__*/_jsx(DayGridEvent, {
          occurrence: placeholder,
          variant: "placeholder"
        })
      })]
    })
  });
}
/**
 * Makes sure any event dropped in the day cell is turned into an all-day event.
 */
function addPropertiesToDroppedEvent() {
  return {
    allDay: true
  };
}